/**
 * One-shot client context snapshot for Ryn Replay (#30211).
 *
 * Emitted once at session start as a `Context` envelope so the player can show
 * Sentry-style chips (browser, OS, viewport, environment) without opening
 * rrweb payloads. Indexable facts (`userAgent`, `environment`) also ride
 * top-level on the envelope — same rule as `url` / `appVersion`: the SQL
 * projection never opens `payload`.
 */

export {
  RYN_REPLAY_CONTEXT_EVENT_TYPE,
  resolveRynReplayEnvironmentLabel,
} from '@ups/xplat/base/ryn-replay';

export interface IRynReplayClientContextSnapshot {
  userAgent: string;
  language: string;
  timeZone: string;
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  devicePixelRatio: number;
  appVersion?: string;
  buildNumber?: string;
  environment?: string;
}

export interface IRynReplayClientContextReadOptions {
  appVersion?: string;
  buildNumber?: string;
  environment?: string;
  win?: Window & typeof globalThis;
}

/**
 * Read the current browser/OS/viewport/env snapshot. Pure enough to unit-test
 * with a stub `win`; production passes nothing and uses `globalThis`.
 */
export const readRynReplayClientContext = (
  options: IRynReplayClientContextReadOptions = {}
): IRynReplayClientContextSnapshot => {
  const win =
    options.win ?? (globalThis as unknown as Window & typeof globalThis);
  const nav = win.navigator as Navigator | undefined;
  const screen = win.screen as Screen | undefined;

  let timeZone = '';
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    timeZone = '';
  }

  return {
    userAgent: nav?.userAgent?.trim() || '',
    language: nav?.language?.trim() || '',
    timeZone,
    screenWidth: screen?.width ?? 0,
    screenHeight: screen?.height ?? 0,
    viewportWidth: win.innerWidth ?? 0,
    viewportHeight: win.innerHeight ?? 0,
    devicePixelRatio: win.devicePixelRatio ?? 1,
    ...(options.appVersion ? { appVersion: options.appVersion } : {}),
    ...(options.buildNumber ? { buildNumber: options.buildNumber } : {}),
    ...(options.environment ? { environment: options.environment } : {}),
  };
};

/** Payload shape for the `Context` envelope (opaque to ingest). */
export const rynReplayClientContextPayload = (
  snapshot: IRynReplayClientContextSnapshot
): Record<string, unknown> => ({
  userAgent: snapshot.userAgent,
  language: snapshot.language,
  timeZone: snapshot.timeZone,
  screenWidth: snapshot.screenWidth,
  screenHeight: snapshot.screenHeight,
  viewportWidth: snapshot.viewportWidth,
  viewportHeight: snapshot.viewportHeight,
  devicePixelRatio: snapshot.devicePixelRatio,
  ...(snapshot.appVersion ? { appVersion: snapshot.appVersion } : {}),
  ...(snapshot.buildNumber ? { buildNumber: snapshot.buildNumber } : {}),
  ...(snapshot.environment ? { environment: snapshot.environment } : {}),
});
